import React, { useContext, useEffect, useState } from "react";
import "./FormCluster.css";
import FormInput from "../Form/FormInput";

import StateDropdown from "../StateDropdown/StateDropdown";
import { getAddressFromPincodeAPI } from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import { CartContext } from "../../../../App";
import ReactGA from "react-ga4";
import { experimentTracker } from "../../../../Utils/ExperimentTracker";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";

type FiredEventsKeys = "address" | "flatNo" | "city" | "state" | "pincode";

const FormCluster = () => {
  const [firedEvents, setFiredEvents] = useState<
    Record<FiredEventsKeys, boolean>
  >({
    address: false,
    flatNo: false,
    city: false,
    state: false,
    pincode: false,
  });
  const { userDetails, setUserDetails } = useContext(CartContext);

  const [address, setAddress] = useState(
    sessionStorage.getItem("address") || ""
  );
  const [flatNo, setFlatNo] = useState(sessionStorage.getItem("flatNo") || "");
  const [city, setCity] = useState(sessionStorage.getItem("city") || "");
  const [state, setState] = useState(sessionStorage.getItem("state") || "");
  const [pincode, setPincode] = useState<string>(
    sessionStorage.getItem("pincode") || ""
  );

  const [error, setError] = useState({
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [loading, setLoading] = useState(false);

  // Add helper function
  const fireGAEventOnce = (fieldName: FiredEventsKeys, action: string) => {
    if (!firedEvents[fieldName]) {
      trackData(action);
      ReactGA.event(
        experimentTracker.addExperimentParams({
          category: "preorder_Form_Interaction",
          action: action,
          label: "preorder_Checkout_Form",
        })
      );
      setFiredEvents((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  const fetchStateAndCity = async (pincode: string) => {
    setLoading(true);
    try {
      const response = await getAddressFromPincodeAPI(pincode);
      console.log(response);
      if (response.data.state && response.data.city) {
        setState(response.data.state);
        setCity(response.data.city);
        sessionStorage.setItem("state", response.data.state);
        sessionStorage.setItem("city", response.data.city);
        setUserDetails({
          ...userDetails,
          state: response.data.state,
          city: response.data.city,
        });
        setError((prevError) => ({ ...prevError, pincode: "" }));
      }
    } catch (error) {
      console.error("Error fetching state and city:", error);
      setError((prevError) => ({
        ...prevError,
        pincode: "Failed to fetch state and city.",
      }));
    } finally {
      setLoading(false);
    }
  };

  // Form change handler
  const handleFormChange = (event: any) => {
    const { value, name } = event.target;

    // Only allow numeric input for pincode
    if (name === "pincode" && /\D/.test(value)) {
      return; // Exit if non-numeric input is detected
    }

    let updatedError = { ...error };
    switch (name) {
      case "address":
        updatedError.address =
          value.length < 5
            ? "Please enter a valid address."
            : value.length > 80
            ? "Address cannot exceed 80 characters."
            : "";
        setAddress(value);
        sessionStorage.setItem("address", value);
        setUserDetails({ ...userDetails, address: value });
        break;

      case "flatNo":
        updatedError.flatNo =
          value.length < 1 ? "Please enter a valid flat/house number." : "";
        setFlatNo(value);
        sessionStorage.setItem("flatNo", value);
        setUserDetails({ ...userDetails, flatNo: value });
        break;

      case "city":
        updatedError.city =
          value.length < 2 ? "Please enter a valid city." : "";
        setCity(value);
        sessionStorage.setItem("city", value);
        setUserDetails({ ...userDetails, city: value });
        break;

      case "state":
        updatedError.state =
          value.length < 2 ? "Please select a valid state." : "";
        setState(value);
        sessionStorage.setItem("state", value);
        setUserDetails({ ...userDetails, state: value });

        break;

      case "pincode":
        updatedError.pincode =
          value.length !== 6 ? "Please enter a valid pincode." : "";
        setPincode(value);
        sessionStorage.setItem("pincode", value);
        setUserDetails({ ...userDetails, pincode: value });

        // Call the API to fetch state and city
        if (value.length === 6) {
          fetchStateAndCity(value);
        }
        break;

      default:
        break;
    }

    setError(updatedError);
  };

  return (
    <div className="parent-wrapper">
      <div className="child-wrapper">
        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length <= 0) {
              setError({
                ...error,
                flatNo: "Please enter a valid flat/house number.",
              });
            } else {
              setError({ ...error, flatNo: "" });
              setUserDetails({ ...userDetails, flatNo: value });
              fireGAEventOnce("flatNo", "FlatNo_Filled");
            }
          }}
          title="Flat No./House No. *"
          value={flatNo}
          onChange={handleFormChange}
          name="flatNo"
          type="text"
          error={error.flatNo}
        />
        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length <= 0) {
              setError({ ...error, address: "Please enter a valid address." });
            } else {
              setError({ ...error, address: "" });
              setUserDetails({ ...userDetails, address: value });
              fireGAEventOnce("address", "Address_Filled");
            }
          }}
          title="Address *"
          value={address}
          onChange={handleFormChange}
          name="address"
          type="text"
          error={error.address}
        />

        <FormInput
          loading={loading}
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length !== 6) {
              setError({ ...error, pincode: "Please enter a valid pincode." });
            } else {
              setError({ ...error, pincode: "" });
              setUserDetails({ ...userDetails, pincode: value });
              fireGAEventOnce("pincode", "Pincode_Filled");
            }
          }}
          title="Pincode *"
          value={pincode}
          onChange={handleFormChange}
          name="pincode"
          type="text"
          error={error.pincode}
        />

        <FormInput
          isVisible={true}
          disabled={false}
          onBlur={(event: any) => {
            const { value } = event?.target;
            if (value.length <= 0) {
              setError({ ...error, city: "Please enter a valid city." });
            } else {
              setError({ ...error, city: "" });
              setUserDetails({ ...userDetails, city: value });
            }
          }}
          title="City *"
          value={city}
          onChange={handleFormChange}
          name="city"
          type="text"
          error={error.city}
        />

        <StateDropdown
          value={state}
          onInputChange={(value: string) => {
            setState(value);
            sessionStorage.setItem("state", value);
            setUserDetails({ ...userDetails, state: value });
          }}
          isVisible={true}
        />
      </div>
    </div>
  );
};

export default FormCluster;
